import { useCallback, useEffect, useMemo, useState } from "react";
import {
  FormControl,
  FormLabel,
  MenuItem,
  Select,
  Stack,
  Step,
  StepButton,
  StepContent,
  Stepper,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import { debug as debugFn } from "debug";
import { ok } from "neverthrow";

import { mustParse } from "@thalamos/common";
import type { CreateSingleUseActionTokenRequest } from "@thalamos/aspire-common";
import {
  SingleUseActionTokenAssignPatientNhsNumberSchema,
  SingleUseActionTokenPatientBulkImportSchema,
} from "@thalamos/aspire-common";

import { apiHooks } from "~/api.js";
import {
  Button,
  Dropdown,
  PopupDialog,
  PopupDialogTitle,
} from "~/components/design-system/index.js";
import type { DialogProps } from "~/hooks/dialogs/index.js";
import { OrganisationPicker } from "~/pages/Admin/OrganisationPicker.js";
import { TeamPicker } from "~/pages/Admin/TeamPicker.js";

const debug = debugFn("aspire-frontend:SingleUseActionTokenPage");

export type CreateTokenDialogProps = DialogProps<
  unknown,
  | { continue: false }
  | {
      continue: true;
      type: CreateSingleUseActionTokenRequest["type"];
      context: CreateSingleUseActionTokenRequest["context"];
    }
>;

export const CreateTokenDialog = ({
  open,
  onClose,
}: CreateTokenDialogProps) => {
  const tokenTypes = useMemo(
    () =>
      [
        {
          type: "assignPatientNhsNumber",
          label: "Bulk Update Patient NHS Numbers",
          Component: AssignPatientNhsNumber,
        },
        {
          type: "patientBulkImport",
          label: "Bulk Import Patients",
          Component: PatientBulkImport,
        },
        {
          type: "bulkUserEmailMigration",
          label: "Bulk Migrate User Emails",
          Component: BulkUserEmailMigration,
        },
        {
          type: "bulkTeamEmailMigration",
          label: "Bulk Migrate Team Emails",
          Component: BulkTeamEmailMigration,
        },
      ] as const,
    [],
  );

  const [activeStep, setActiveStep] = useState(0);

  const [type, setType] = useState<null | (typeof tokenTypes)[number]["type"]>(
    null,
  );

  const [context, setContext] = useState<
    CreateSingleUseActionTokenRequest["context"] | null
  >(null);
  const SelectedComponent = useMemo(() => {
    const found = tokenTypes.find((tt) => tt.type === type);
    return found ? found.Component : null;
  }, [type, tokenTypes]);

  const handleClose = useCallback(() => {
    void onClose({
      continue: false,
    });
  }, [onClose]);

  const handleContinue = useCallback(() => {
    if (type !== null && context !== null) {
      void onClose({
        continue: true,
        type,
        context,
      });
    }
  }, [context, onClose, type]);

  return (
    <PopupDialog open={open} onClose={handleClose}>
      <PopupDialogTitle
        closeDialog={handleClose}
        titleText={"Create Single Use Action Token"}
      />
      <Stack
        sx={{
          justifyContent: "stretch",
          alignItems: "left",
          gap: 1,
        }}
      >
        <Stepper orientation="vertical" nonLinear activeStep={activeStep}>
          <Step expanded completed={type !== null}>
            <StepButton color="inherit">Select an Action type</StepButton>
            <StepContent>
              <Select
                fullWidth
                onChange={(e) => {
                  setType(
                    e.target.value as (typeof tokenTypes)[number]["type"],
                  );
                  setActiveStep(1);
                }}
                value={type}
              >
                {tokenTypes.map((tokenType) => (
                  <MenuItem key={tokenType.type} value={tokenType.type}>
                    {tokenType.label}
                  </MenuItem>
                ))}
              </Select>
            </StepContent>
          </Step>
          <Step disabled={type === null}>
            <StepButton
              color="inherit"
              onClick={() => {
                setActiveStep(1);
              }}
            >
              Configure the Action
            </StepButton>
            <StepContent>
              {SelectedComponent && (
                <SelectedComponent context={context} setContext={setContext} />
              )}
            </StepContent>
          </Step>
        </Stepper>
        <Stack
          direction="row"
          spacing={2}
          sx={{
            justifyContent: "center",
            mt: 2,
          }}
        >
          <Button variant="outlined" label={"Close"} onClick={handleClose} />
          <Button
            variant="contained"
            label={"Create"}
            disabled={type === null || context === null}
            onClick={handleContinue}
          />
        </Stack>
      </Stack>
    </PopupDialog>
  );
};

type TokenConfigProps = {
  context: CreateSingleUseActionTokenRequest["context"] | null;
  setContext: (
    context: CreateSingleUseActionTokenRequest["context"] | null,
  ) => void;
};

const AssignPatientNhsNumber = ({
  //   context,
  setContext,
}: TokenConfigProps) => {
  const [teamId, setTeamId] = useState<string | null>(null);

  useEffect(() => {
    // Ensure it parses correctly
    if (teamId !== null) {
      const result = ok({
        type: "assignPatientNhsNumber" as const,
        context: { teamId },
      })
        .andThen(mustParse(SingleUseActionTokenAssignPatientNhsNumberSchema))
        .orTee((err) => {
          debug(err.prettifyError());
        });

      setContext(result.isOk() ? result.value.context : null);
    } else {
      setContext(null);
    }
  }, [setContext, teamId]);

  return (
    <Stack
      sx={{
        gap: 2,
      }}
    >
      <div>
        Create a link for a client to bulk import patient NHS numbers. This link
        must be used by a user of the Team whose ID is input below.
      </div>
      <TextField
        label="Team ID"
        value={teamId}
        onChange={(e) => {
          setTeamId(e.target.value);
        }}
      />
    </Stack>
  );
};

const PatientBulkImport = ({ setContext }: TokenConfigProps) => {
  const [team, setTeam] = useState({
    id: null as string | null,
    name: "",
  });
  const [externalSystemType, setExternalSystemType] = useState<"pds" | "rio">(
    "rio",
  );
  const [rioInstanceSelected, setRioInstanceSelected] = useState<string | null>(
    null,
  );
  const [trustName, setTrustName] = useState("");
  const [trustOdsCode, setTrustOdsCode] = useState("");

  const [{ data }] = apiHooks.rio.search("", 1000, 0);
  const rioInstances = data?.results ?? [];

  useEffect(() => {
    const teamId = team.id;
    const externalSystem =
      externalSystemType === "rio" && rioInstanceSelected
        ? `rio/${rioInstanceSelected}`
        : externalSystemType === "pds"
          ? "pds/00000000-00000000-00000000-00000000"
          : null;

    if (teamId && externalSystem) {
      const result = ok({
        type: "patientBulkImport" as const,
        context: {
          teamId,
          externalSystem,
          trust:
            trustName && trustOdsCode
              ? { name: trustName, odsCode: trustOdsCode }
              : undefined,
        },
      })
        .andThen(mustParse(SingleUseActionTokenPatientBulkImportSchema))
        .orTee((err) => {
          debug(err.prettifyError());
        });

      setContext(result.isOk() ? result.value.context : null);
    } else {
      setContext(null);
    }
  }, [
    setContext,
    team.id,
    externalSystemType,
    rioInstanceSelected,
    trustName,
    trustOdsCode,
  ]);

  return (
    <Stack sx={{ gap: 2 }}>
      <div>
        Create a link for a client to bulk import patients and patient MHA
        statuses. This link must be used by a user of the Team selected below.
      </div>
      <TeamPicker
        idField={"id"}
        nameField={"name"}
        values={team}
        setValues={setTeam}
        error={null}
        label="Team (*)"
      />
      <FormControl>
        <FormLabel sx={{ fontWeight: "bold", mb: "0.5em" }}>
          External System Type (*)
        </FormLabel>
        <ToggleButtonGroup
          color="primary"
          value={externalSystemType}
          exclusive
          onChange={(_, value) => {
            if (value !== null) {
              setExternalSystemType(value as "pds" | "rio");
            }
          }}
          aria-label="External system type"
        >
          <ToggleButton value="rio">Rio</ToggleButton>
          <ToggleButton value="pds">PDS</ToggleButton>
        </ToggleButtonGroup>
      </FormControl>
      {externalSystemType === "rio" && (
        <Dropdown
          name="rioInstance"
          onChange={(e: string) => {
            setRioInstanceSelected(
              rioInstances.find((r) => r.instanceId === e)?.instanceId ?? null,
            );
          }}
          label="RIO Instance (*)"
          selectedValue={rioInstanceSelected ?? ""}
          values={rioInstances.map((r) => ({
            value: r.instanceId,
            label: r.displayName,
          }))}
        />
      )}
      <TextField
        label="Trust Name"
        value={trustName}
        onChange={(e) => {
          setTrustName(e.target.value);
        }}
      />
      <TextField
        label="Trust ODS Code"
        value={trustOdsCode}
        onChange={(e) => {
          setTrustOdsCode(e.target.value);
        }}
      />
    </Stack>
  );
};

const BulkEmailMigrationConfig = ({
  setContext,
  description,
}: TokenConfigProps & { description: string }) => {
  const [organisation, setOrganisation] = useState<{
    id: string | null;
    name: string;
  }>({ id: null, name: "" });

  useEffect(() => {
    // The OrganisationPicker only sets `id` to a real organisation's UUID, so
    // the presence of an id is sufficient; the server re-validates the org.
    setContext(organisation.id ? { organisationId: organisation.id } : null);
  }, [setContext, organisation.id]);

  return (
    <Stack sx={{ gap: 2 }}>
      <div>{description}</div>
      <OrganisationPicker
        idField={"id"}
        nameField={"name"}
        values={organisation}
        setValues={setOrganisation}
        error={null}
        label="Organisation (*)"
      />
    </Stack>
  );
};

const BulkUserEmailMigration = (props: TokenConfigProps) => (
  <BulkEmailMigrationConfig
    {...props}
    description="Create a link to bulk migrate user account email addresses for the selected organisation. The migration is scoped to this organisation's users."
  />
);

const BulkTeamEmailMigration = (props: TokenConfigProps) => (
  <BulkEmailMigrationConfig
    {...props}
    description="Create a link to bulk migrate team email addresses for the selected organisation. The migration is scoped to this organisation's teams."
  />
);
